import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus } from "lucide-react";
import { useTranslation } from "@/lib/i18n";
import StockTickerPicker from "@/components/portfolio/StockTickerPicker";
import InfoTooltip from "@/components/portfolio/InfoTooltip";
import { fmtCurrency, fmtNumber } from "@/components/portfolio/_format";

/**
 * Phase 8.A QA-3 — Add / Edit / Add-to-Existing holding dialog.
 *
 * Three modes:
 *
 *   * ``add``           — pick a brand-new ticker. If the picked ticker
 *                         is ALREADY held, the dialog seamlessly switches
 *                         to ``add_purchase`` mode (no blocking error).
 *   * ``edit``          — adjust the persisted shares/buy_price/notes for
 *                         an existing holding. Ticker picker is locked.
 *   * ``add_purchase``  — internal mode entered when the chosen ticker
 *                         in ``add`` matches one of ``existingPositions``.
 *                         Shows the current shares + average buy price,
 *                         live-projects the post-merge total shares and
 *                         new share-weighted average buy price, and
 *                         submits with the same POST /portfolio/holdings
 *                         endpoint (backend merges on the server side).
 *
 * The dialog accepts the parent's ``existingPositions`` array (typed
 * `{ ticker, shares, buy_price, currency }`) so duplicate detection
 * happens entirely client-side — the user never sees a 4xx blocking
 * error for adding more of a ticker they already own.
 */
export default function HoldingDialog({
  open,
  onOpenChange,
  mode = "add",
  initial = null,
  busy = false,
  errorMessage = null,
  onSubmit,
  existingPositions = [],
}) {
  const { t } = useTranslation();

  const [selectedStock, setSelectedStock] = useState(null);
  const [shares, setShares] = useState("");
  const [buyPrice, setBuyPrice] = useState("");
  const [buyDate, setBuyDate] = useState("");
  const [notes, setNotes] = useState("");
  const [fees, setFees] = useState("");
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setSelectedStock({
        ticker: (initial.ticker || "").toUpperCase(),
        name: initial.company_name || null,
        exchange: initial.exchange || null,
        currency: initial.currency || null,
        sector: initial.sector || null,
      });
      setShares(initial.shares !== undefined && initial.shares !== null ? String(initial.shares) : "");
      setBuyPrice(initial.buy_price !== undefined && initial.buy_price !== null ? String(initial.buy_price) : "");
      setBuyDate(initial.buy_date || "");
      setNotes(initial.notes || "");
    } else {
      setSelectedStock(null);
      setShares("");
      setBuyPrice("");
      setBuyDate("");
      setNotes("");
    }
    setFees("");
    setLocalError(null);
  }, [open, initial]);

  // Derived: does the (selected) ticker already exist in the user's
  // portfolio? When YES (and we're not in edit mode) we switch the
  // dialog into the "Add to Existing Position" UX. Lookup is
  // case-insensitive on the ticker symbol.
  const existingPosition = useMemo(() => {
    if (mode === "edit" || !selectedStock?.ticker) return null;
    const want = String(selectedStock.ticker).toUpperCase();
    return (existingPositions || []).find(
      (p) => String(p.ticker || "").toUpperCase() === want,
    ) || null;
  }, [selectedStock?.ticker, existingPositions, mode]);

  const effectiveMode =
    mode === "edit" ? "edit"
      : existingPosition ? "add_purchase"
      : "add";
  const isEdit = effectiveMode === "edit";
  const isAddPurchase = effectiveMode === "add_purchase";

  // Numeric parsing.
  const sharesNum = parseFloat(shares);
  const buyNum = parseFloat(buyPrice);
  const feesNum = parseFloat(fees);
  const sharesValid = Number.isFinite(sharesNum) && sharesNum > 0;
  const buyValid = Number.isFinite(buyNum) && buyNum > 0;
  const feesValid = !fees || (Number.isFinite(feesNum) && feesNum >= 0);
  const tickerValid = !!(selectedStock && selectedStock.ticker);
  const submitEnabled = tickerValid && sharesValid && buyValid && feesValid && !busy;

  // Live projection for "Add to Existing Position" mode.
  const projection = useMemo(() => {
    if (!isAddPurchase || !existingPosition) return null;
    if (!sharesValid || !buyValid) return null;
    const oldShares = Number(existingPosition.shares) || 0;
    const oldBuy = Number(existingPosition.buy_price) || 0;
    const totalShares = oldShares + sharesNum;
    if (totalShares <= 0) return null;
    const totalCost = oldShares * oldBuy + sharesNum * buyNum;
    const newAvg = totalCost / totalShares;
    return { totalShares, newAvg };
  }, [isAddPurchase, existingPosition, sharesValid, buyValid, sharesNum, buyNum]);

  const titleText = isAddPurchase
    ? t("portfolio.dialog.title_add_purchase")
    : isEdit
      ? t("portfolio.dialog.title_edit")
      : t("portfolio.dialog.title_add");

  const descText = isAddPurchase
    ? t("portfolio.dialog.description_add_purchase", { ticker: existingPosition?.ticker || selectedStock?.ticker || "" })
    : t("portfolio.dialog.description");

  const submitLabel = busy
    ? t("portfolio.dialog.saving")
    : isEdit
      ? t("portfolio.dialog.save")
      : isAddPurchase
        ? t("portfolio.dialog.add_purchase")
        : t("portfolio.dialog.add");

  const sharesLabel = isAddPurchase ? t("portfolio.dialog.shares_new") : t("portfolio.dialog.shares");
  const buyPriceLabel = isAddPurchase ? t("portfolio.dialog.buy_price_new") : t("portfolio.dialog.buy_price");

  const currency = existingPosition?.currency || selectedStock?.currency || "USD";

  const submit = (e) => {
    e?.preventDefault?.();
    setLocalError(null);
    if (!tickerValid) {
      setLocalError(t("portfolio.dialog.err_select_stock"));
      return;
    }
    if (!sharesValid) { setLocalError(t("portfolio.dialog.err_shares")); return; }
    if (!buyValid) { setLocalError(t("portfolio.dialog.err_buy_price")); return; }
    onSubmit?.({
      ticker: selectedStock.ticker,
      shares: sharesNum,
      buy_price: buyNum,
      buy_date: buyDate || undefined,
      notes: notes ? notes : undefined,
      fees: feesValid && fees ? feesNum : undefined,
      company_name: selectedStock.name || undefined,
      exchange: selectedStock.exchange || undefined,
      currency: selectedStock.currency || undefined,
      sector: selectedStock.sector || undefined,
      // Force backend to merge for add+existing flow. The backend
      // default is also "merge" but we send this explicitly so the
      // intent is unambiguous from the request payload.
      merge_behavior: isAddPurchase ? "merge" : "merge",
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="portfolio-holding-dialog" data-mode={effectiveMode} className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle data-testid="portfolio-holding-dialog-title">{titleText}</DialogTitle>
          <DialogDescription>{descText}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="holding-ticker">{t("portfolio.dialog.ticker")}</Label>
            <StockTickerPicker
              value={selectedStock}
              onSelect={setSelectedStock}
              onClear={() => setSelectedStock(null)}
              disabled={isEdit}
              autoFocus={!isEdit}
              placeholder={t("portfolio.dialog.ticker_placeholder")}
            />
            {!isEdit && !tickerValid ? (
              <div className="text-[11px] text-muted-foreground" data-testid="portfolio-holding-select-stock-hint">
                {t("portfolio.dialog.hint_select_stock")}
              </div>
            ) : null}
          </div>

          {/* Existing-position panel — visible only in add_purchase mode. */}
          {isAddPurchase && existingPosition ? (
            <div
              data-testid="portfolio-holding-existing-position"
              className="rounded-md border border-[#3DEBFF]/30 bg-[rgba(61,235,255,0.06)] px-3 py-2 text-xs grid grid-cols-2 gap-x-3 gap-y-1"
            >
              <div className="col-span-2 text-[10px] uppercase tracking-wider text-[#3DEBFF]">
                {t("portfolio.dialog.existing_position")}
              </div>
              <div className="text-muted-foreground">{t("portfolio.dialog.existing_shares")}</div>
              <div className="text-right tabular" data-testid="portfolio-holding-existing-shares">
                {fmtNumber(existingPosition.shares, { digits: 6 })}
              </div>
              <div className="text-muted-foreground">{t("portfolio.dialog.existing_avg_buy")}</div>
              <div className="text-right tabular" data-testid="portfolio-holding-existing-buy-price">
                {fmtCurrency(existingPosition.buy_price, { currency })}
              </div>
            </div>
          ) : null}

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="holding-shares">{sharesLabel}</Label>
              <Input
                id="holding-shares"
                data-testid="portfolio-holding-shares-input"
                inputMode="decimal"
                value={shares}
                onChange={(e) => setShares(e.target.value)}
                placeholder={isAddPurchase ? "0.5" : "10"}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="holding-buy-price">{buyPriceLabel}</Label>
              <Input
                id="holding-buy-price"
                data-testid="portfolio-holding-buy-price-input"
                inputMode="decimal"
                value={buyPrice}
                onChange={(e) => setBuyPrice(e.target.value)}
                placeholder="150.00"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="holding-buy-date" className="flex items-center gap-1.5">
                <span>{t("portfolio.dialog.buy_date")}</span>
                <span className="text-muted-foreground text-xs">({t("portfolio.dialog.optional")})</span>
                <InfoTooltip
                  text={t("portfolio.dialog.buy_date_tooltip")}
                  label={t("portfolio.dialog.buy_date_tooltip_label")}
                  testId="portfolio-holding-buy-date-tooltip"
                  side="top"
                />
              </Label>
              <Input
                id="holding-buy-date"
                data-testid="portfolio-holding-buy-date-input"
                type="date"
                value={buyDate}
                onChange={(e) => setBuyDate(e.target.value)}
              />
            </div>
            {!isEdit ? (
              <div className="grid gap-1.5">
                <Label htmlFor="holding-fees">
                  {t("portfolio.dialog.fees")} <span className="text-muted-foreground text-xs">({t("portfolio.dialog.optional")})</span>
                </Label>
                <Input
                  id="holding-fees"
                  data-testid="portfolio-holding-fees-input"
                  inputMode="decimal"
                  value={fees}
                  onChange={(e) => setFees(e.target.value)}
                  placeholder="0.00"
                />
              </div>
            ) : null}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="holding-notes">
              {t("portfolio.dialog.notes")} <span className="text-muted-foreground text-xs">({t("portfolio.dialog.optional")})</span>
            </Label>
            <Textarea
              id="holding-notes"
              data-testid="portfolio-holding-notes-textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={400}
              rows={2}
            />
          </div>

          {/* Live projection panel — only when add_purchase + valid inputs. */}
          {isAddPurchase && projection ? (
            <div
              data-testid="portfolio-holding-projection"
              className="rounded-md border border-[#2BFF8A]/30 bg-[rgba(43,255,138,0.06)] px-3 py-2 text-xs grid grid-cols-2 gap-x-3 gap-y-1"
            >
              <div className="col-span-2 text-[10px] uppercase tracking-wider text-[#2BFF8A] flex items-center gap-1">
                <Plus className="w-3 h-3" />
                {t("portfolio.dialog.projected_section")}
              </div>
              <div className="text-muted-foreground">{t("portfolio.dialog.projected_shares")}</div>
              <div className="text-right tabular" data-testid="portfolio-holding-projected-shares">
                {fmtNumber(projection.totalShares, { digits: 6 })}
              </div>
              <div className="text-muted-foreground">{t("portfolio.dialog.projected_avg_buy")}</div>
              <div className="text-right tabular" data-testid="portfolio-holding-projected-avg-buy">
                {fmtCurrency(projection.newAvg, { currency })}
              </div>
            </div>
          ) : null}

          {(localError || errorMessage) ? (
            <div
              data-testid="portfolio-holding-error"
              className="text-sm text-[#FF3DA6] bg-[#FF3DA6]/10 border border-[#FF3DA6]/30 rounded-md px-3 py-2"
            >
              {localError || errorMessage}
            </div>
          ) : null}

          <DialogFooter className="mt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)} data-testid="portfolio-holding-cancel-button">
              {t("portfolio.dialog.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={!submitEnabled}
              data-testid="portfolio-holding-submit-button"
            >
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
